import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Paper,
  Divider,
  Breadcrumbs,
  Link as MuiLink
} from '@mui/material';
import { Link } from 'react-router-dom';
import LandingHeader from '../components/LandingHeader';
import Footer from '../components/Footer';

const CookiesPage: React.FC = () => {
  return (
    <Box>
      {/* Hero Section */}
      <Box sx={{ bgcolor: '#1a365d', color: 'white', py: 6, pt: 4 }}>
        <LandingHeader />
        <Container maxWidth="lg">
          <Typography variant="h2" component="h1" fontWeight="bold" gutterBottom>
            Cookie Policy
          </Typography>
          <Typography variant="h6" sx={{ maxWidth: '700px', mb: 4, opacity: 0.9 }}>
            How we use cookies and similar technologies
          </Typography>
          
          <Breadcrumbs aria-label="breadcrumb" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            <MuiLink component={Link} to="/" color="inherit">
              Home
            </MuiLink>
            <Typography color="rgba(255,255,255,0.9)">Cookie Policy</Typography>
          </Breadcrumbs>
        </Container>
      </Box>
      
      {/* Content */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, borderRadius: 2 }}>
          <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
            Cookie Policy
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom sx={{ mb: 4 }}>
            Last updated: July 1, 2023
          </Typography>
          
          <Typography variant="body1" paragraph>
            This Cookie Policy explains how Contract AI ("we", "us", and "our") uses cookies and similar technologies to recognize you when you visit our website at contractai.com ("Website"). It explains what these technologies are and why we use them, as well as your rights to control our use of them.
          </Typography>
          
          <Typography variant="h5" component="h3" fontWeight="bold" sx={{ mt: 4, mb: 2 }}>
            What are cookies?
          </Typography>
          <Typography variant="body1" paragraph>
            Cookies are small data files that are placed on your computer or mobile device when you visit a website. Cookies are widely used by website owners in order to make their websites work, or to work more efficiently, as well as to provide reporting information.
          </Typography>
          <Typography variant="body1" paragraph>
            Cookies set by the website owner (in this case, Contract AI) are called "first-party cookies". Cookies set by parties other than the website owner are called "third-party cookies". Third-party cookies enable third-party features or functionality to be provided on or through the website (e.g., advertising, interactive content, and analytics). The parties that set these third-party cookies can recognize your computer both when it visits the website in question and also when it visits certain other websites.
          </Typography>
          
          <Typography variant="h5" component="h3" fontWeight="bold" sx={{ mt: 4, mb: 2 }}>
            Why do we use cookies?
          </Typography>
          <Typography variant="body1" paragraph>
            We use first- and third-party cookies for several reasons. Some cookies are required for technical reasons in order for our Website to operate, and we refer to these as "essential" or "strictly necessary" cookies. Other cookies also enable us to track and target the interests of our users to enhance the experience on our Online Properties. Third parties serve cookies through our Website for advertising, analytics, and other purposes. This is described in more detail below.
          </Typography>
          
          <Typography variant="h5" component="h3" fontWeight="bold" sx={{ mt: 4, mb: 2 }}>
            The specific types of first- and third-party cookies served through our Website
          </Typography>
          
          <Typography variant="h6" component="h4" fontWeight="bold" sx={{ mt: 3, mb: 1 }}>
            Essential cookies
          </Typography>
          <Typography variant="body1" paragraph>
            These cookies are strictly necessary to provide you with services available through our Website and to use some of its features, such as access to secure areas. Because these cookies are strictly necessary to deliver the Website, you cannot refuse them without impacting how our Website functions.
          </Typography>
          
          <Typography variant="h6" component="h4" fontWeight="bold" sx={{ mt: 3, mb: 1 }}>
            Performance and functionality cookies
          </Typography>
          <Typography variant="body1" paragraph>
            These cookies are used to enhance the performance and functionality of our Website but are non-essential to their use. However, without these cookies, certain functionality may become unavailable.
          </Typography>
          
          <Typography variant="h6" component="h4" fontWeight="bold" sx={{ mt: 3, mb: 1 }}>
            Analytics and customization cookies
          </Typography>
          <Typography variant="body1" paragraph>
            These cookies collect information that is used either in aggregate form to help us understand how our Website is being used or how effective our marketing campaigns are, or to help us customize our Website for you.
          </Typography>
          
          <Typography variant="h6" component="h4" fontWeight="bold" sx={{ mt: 3, mb: 1 }}>
            Advertising cookies
          </Typography>
          <Typography variant="body1" paragraph>
            These cookies are used to make advertising messages more relevant to you. They perform functions like preventing the same ad from continuously reappearing, ensuring that ads are properly displayed for advertisers, and in some cases selecting advertisements that are based on your interests.
          </Typography>
          
          <Typography variant="h5" component="h3" fontWeight="bold" sx={{ mt: 4, mb: 2 }}>
            How can you control cookies?
          </Typography>
          <Typography variant="body1" paragraph>
            You have the right to decide whether to accept or reject cookies. You can exercise your cookie rights by setting your preferences in the Cookie Consent Manager. The Cookie Consent Manager allows you to select which categories of cookies you accept or reject. Essential cookies cannot be rejected as they are strictly necessary to provide you with services.
          </Typography>
          <Typography variant="body1" paragraph>
            If you choose to reject cookies, you may still use our website though your access to some functionality and areas of our website may be restricted. You may also set or amend your web browser controls to accept or refuse cookies.
          </Typography>
          
          <Typography variant="h5" component="h3" fontWeight="bold" sx={{ mt: 4, mb: 2 }}>
            How often will we update this Cookie Policy?
          </Typography>
          <Typography variant="body1" paragraph>
            We may update this Cookie Policy from time to time in order to reflect, for example, changes to the cookies we use or for other operational, legal, or regulatory reasons. Please therefore re-visit this Cookie Policy regularly to stay informed about our use of cookies and related technologies.
          </Typography>
          <Typography variant="body1" paragraph>
            The date at the top of this Cookie Policy indicates when it was last updated.
          </Typography>
          
          <Typography variant="h5" component="h3" fontWeight="bold" sx={{ mt: 4, mb: 2 }}>
            Where can you get further information?
          </Typography>
          <Typography variant="body1" paragraph>
            If you have any questions about our use of cookies or other technologies, please email us at privacy@contractai.com.
          </Typography>
        </Paper>
      </Container>
      
      <Footer />
    </Box>
  );
};

export default CookiesPage; 